/**
 * VoiceInterview.js
 * TB-104 Phase 2 — ElevenLabs real-time voice interview surface.
 *
 * What this does:
 *   1. Fetches a short-lived conversation token from the AI Lambda (POST /ai/voice-token).
 *   2. Starts a WebRTC voice session via @elevenlabs/react.
 *   3. Accumulates the live transcript from onMessage callbacks.
 *   4. On "Done — write it up", collects the user's turns and feeds them into
 *      the existing generateStInterviewStory pipeline — exactly the same payoff
 *      as the text interview.
 *
 * Design contract:
 *   - Matches the .iv-* aesthetic from StRecordPage (Lora, warm gold/cream palette,
 *     fixed dock feel, bubble styles).
 *   - No em-dashes in copy (project rule).
 *   - No vendor names in user-facing text.
 *   - Mobile-first: safe-area insets, minimum 44px tap targets, fixed dock.
 *   - All state is local to this component. Parent receives a transcript via onTranscript
 *     callback only when the user taps "Done".
 */

import React from 'react';
import { ConversationProvider, useConversation } from '@elevenlabs/react';
import { callAi } from '../utils/aiClient';
import { classifyAnswer, hasDoneSignalWide, DONE_SIGNAL_CLOSE_LINE, DONE_BUTTON_LABEL, buildOpenerLine, COLD_START_OPENER, FINISH_CARD_LINE,
  voiceWindDownSchedule, isGriefOrCrisisConversation,
  isSilentTurn, VOICE_SILENCE_FINISH_SEC } from '../utils/interviewEngine'; // F-75 graceful landing; F-263 silence backstop
import { trackEvent } from '../utils/analytics';
import {
  EXIT_GUARD_TITLE, EXIT_GUARD_SUB, EXIT_GUARD_LEAVE, DOCK_HELPER, BELOW_FLOOR_NUDGE,
  CTA_PRIMARY, CTA_SECONDARY, EXIT_GUARD_SCRIM, EXIT_GUARD_CARD,
} from '../constants/interviewExitGuard'; // F-103 universal exit guard (shared with StRecordPage)
import {
  typedShellInsets,
  applyThreadScroll,
  classifyMicPreflight,
  isVoiceConnectingTimeout,
  isVoicePreCallPhase,
  desktopSidebarPx,
  VOICE_READY,
} from '../utils/interviewChatShell';

// PIPE-SAVE-1: when the storyteller SPEAKS a done/write-up directive, run the same finish->save
// handoff the Done button uses (finishUp -> onDone -> generateStInterviewStory -> auto-save). A short
// delay lets Pip's intent-only spoken close play before we end the session; the frontend still owns
// the authoritative "saved" confirmation once the save lands.
const DONE_SIGNAL_FINISH_DELAY_MS = 3500;

// save_story build (2026-07-10) — pure, exported helpers (unit-tested in VoiceInterview.autofinish.test.js).

// Normalize a spoken line for sentinel matching: lowercase, collapse whitespace, strip trailing
// terminal punctuation. Keeps internal punctuation (so the constant matches as a whole).
export function normalizeSpoken(text) {
  return (text || '').toLowerCase().replace(/\s+/g, ' ').trim().replace(/[.!?…]+$/g, '').trim();
}

// BN1 SENTINEL: strict, NORMALIZED `contains` of the shared DONE_SIGNAL_CLOSE_LINE constant — never
// fuzzy/intent matching. When Pip speaks this exact close line (because the model called save_story OR
// the floor stopped), it is the authoritative save election relayed through the ElevenLabs transcript.
export function matchesSaveCloseSentinel(aiMessage) {
  const sentinel = normalizeSpoken(DONE_SIGNAL_CLOSE_LINE);
  return !!sentinel && normalizeSpoken(aiMessage).includes(sentinel);
}

// S2 OBJECTION WINDOW: a confirmation lets a pending finish proceed; an explicit objection OR a
// substantive continuation cancels it. Governing invariant: never end a story early, so anything that
// is not a clear confirmation and carries real content keeps the interview going.
export function isConfirmation(text) {
  const t = (text || '').toLowerCase().trim();
  return /^(yes|yeah|yep|yup|sure|ok|okay|okey|alright|all right|right|correct|go ahead|do it|please do|that'?s it|that'?s all|sounds good|perfect|mm-?hmm?|uh-?huh)\b/.test(t);
}
export function shouldCancelPendingFinish(text, substantiveWordCount) {
  const t = (text || '').toLowerCase().trim();
  if (/\b(wait|hold on|hang on|hold up|not yet|not done|one more|another|actually|before that|but first)\b/.test(t)) return true; // explicit objection
  if (/^no\b/.test(t)) return true;
  if (isConfirmation(t)) return false;        // confirmation -> let the pending finish proceed
  return (substantiveWordCount || 0) >= 4;    // substantive continuation -> keep going (never end early)
}

// ---------------------------------------------------------------------------
// Token fetch — POSTs the `voice_token` action to the default AI route (/story),
// which the remnantAI Lambda dispatches by action (Cognito-gated). callAi attaches
// the Cognito JWT and throws a clear message if the user isn't signed in.
// ---------------------------------------------------------------------------
async function fetchVoiceToken(selectedPromptText) {
  const data = await callAi(
    { action: 'voice_token', promptText: selectedPromptText || '' },
  );
  if (!data || !data.conversationToken) {
    throw new Error('No conversation token received.');
  }
  return data.conversationToken;
}

// Server-side transcript fetch — ElevenLabs' stored record of the call. The live client
// stream is unreliable for the user's transcribed speech; this is the source of truth.
// Returns turns as [{ role:'user'|'assistant', content }].
async function fetchVoiceTranscript(conversationId) {
  const data = await callAi({ action: 'voice_transcript', conversationId });
  return (data && Array.isArray(data.turns)) ? data.turns : [];
}

// ---------------------------------------------------------------------------
// buildVoiceOpener — the spoken first line, built from the question the user chose.
// Passed to startSession as a first-message override so the interviewer opens with
// THE USER'S prompt, not the agent's hardcoded default.
//
// NEVER RETURNS EMPTY. F-44 closed the old `return ''` hole; every branch ends in a real line, and
// the no-seed paths bottom out at COLD_START_OPENER. (This comment previously said it returned '' for
// an open-ended start "in which case we let the agent use its own opener" — pre-F-44 behaviour, left
// stale for months. It read as an INTENDED path, which is an invitation to reintroduce the hole, so it
// is corrected here rather than left to mislead. F-262b.)
//
// WHY EMPTY WOULD MATTER: the caller spreads the override conditionally —
// `...(opener ? { overrides: { agent: { firstMessage: opener } } } : {})` — so a falsy opener sends NO
// override and ElevenLabs speaks its OWN dashboard first_message. That is a fallback of last resort,
// not a feature: a memorial interview would open by asking about a car. The dashboard value is kept in
// sync with COLD_START_OPENER so that even that path degrades to the intended cold-start greeting;
// `npm run check:voice-agent` reports it. Pinned by the never-empty property test in
// VoiceInterview.opener.test.js — if you add a branch here, it must return a real line.
// ---------------------------------------------------------------------------
export function buildVoiceOpener(selectedPrompt, isGiftBook, recipientName, lang = 'en', savedStoryCount = 0, occasionId = '') {
  const name = (recipientName || '').trim();
  // F-58 dignity fix: a memorial (IN_MEMORY) Celebration book is ABOUT someone who has died —
  // "putting together something special FOR [X]" reads as if [X] will receive it. Memorial books
  // open "a book in memory of [X]" instead. ONLY the IN_MEMORY prefix changes; every other
  // occasion keeps the existing greeting byte-for-byte.
  const isMemorial = occasionId === 'IN_MEMORY';
  // Spanish (bilingual): the English prompt packs aren't translated yet, so when the
  // storyteller picks Español we open with a warm, prompt-agnostic Spanish greeting and let
  // the engine follow them from there. Always returns a non-empty opener so Pip greets in Spanish.
  if (lang === 'es') {
    if (isGiftBook && name) {
      return isMemorial
        ? `¡Hola! Estamos preparando un libro en memoria de ${name}. Me encantaría escuchar una historia de su vida. Cuéntame lo que tú quieras, por donde te nazca empezar.`
        : `¡Hola! Estamos preparando algo muy especial para ${name}. Me encantaría escuchar una historia de su vida. Cuéntame lo que tú quieras, por donde te nazca empezar.`;
    }
    return `¡Hola! Me encantaría escuchar una historia de tu vida. Cuéntame lo que tú quieras: un recuerdo, una persona, un lugar especial. ¿Por dónde te gustaría empezar?`;
  }
  const q = (selectedPrompt?.text || '').trim();
  // F-44 (Just Talk, 1a): NO seed → the cold-start hello (was the silent `return ''` hole). Same seedless
  // pattern the Spanish branch above already uses.
  if (!q) return COLD_START_OPENER;
  if (isGiftBook && name) {
    return isMemorial
      ? `Hi there. We're putting together a book in memory of ${name}. ${q}`
      : `Hi there. We're putting together something special for ${name}. ${q}`;
  }
  // F-24 ratified opener with graduation (removes the canned "I'd love to hear this story").
  return buildOpenerLine(q, { savedStoryCount, variantSeed: savedStoryCount });
}

// ---------------------------------------------------------------------------
// OrbIcon — the animated orb that breathes when the AI is speaking.
// Pure CSS animation, no external deps.
// ---------------------------------------------------------------------------
const OrbIcon = ({ isSpeaking, isConnected }) => (
  <div
    aria-hidden="true"
    style={{
      width: 96,
      height: 96,
      borderRadius: '50%',
      background: isConnected
        ? 'radial-gradient(circle at 38% 36%, rgba(201,168,76,0.9) 0%, rgba(138,109,58,0.8) 55%, rgba(100,78,36,0.95) 100%)'
        : 'radial-gradient(circle at 38% 36%, rgba(180,174,166,0.6) 0%, rgba(120,116,110,0.5) 100%)',
      boxShadow: isSpeaking
        ? '0 0 0 12px rgba(201,168,76,0.18), 0 0 0 24px rgba(201,168,76,0.08), 0 4px 24px rgba(122,90,16,0.3)'
        : isConnected
          ? '0 0 0 6px rgba(201,168,76,0.10), 0 4px 16px rgba(122,90,16,0.18)'
          : '0 4px 12px rgba(15,23,42,0.12)',
      transition: 'box-shadow 0.35s ease, transform 0.35s ease',
      transform: isSpeaking ? 'scale(1.08)' : 'scale(1)',
      margin: '0 auto',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {/* Sound-wave bars inside orb */}
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', height: 28 }}>
      {[0, 0.15, 0.3, 0.15, 0].map((delay, i) => (
        <div
          key={i}
          style={{
            width: 4,
            borderRadius: 2,
            background: 'rgba(255,253,248,0.9)',
            height: isSpeaking ? `${[14, 22, 28, 22, 14][i]}px` : '8px',
            transition: 'height 0.25s ease',
            animationDelay: `${delay}s`,
          }}
        />
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// PipAvatar — Pip reacting in real time during the call. Swaps pose by state:
// idle / listening (you talk) / speaking (Pip asks) / thinking (writing up) /
// delighted (great story). Art lives in /public/pip/.
// ---------------------------------------------------------------------------
const PIP_POSE = {
  idle: 'state-idle',
  greet: 'state-greet',
  listening: 'state-listening',
  speaking: 'state-speaking',
  thinking: 'state-thinking',
  delighted: 'state-delighted',
};
const PipAvatar = ({ pose = 'idle', size = 150, speaking = false }) => (
  <div style={{
    width: size, height: size, position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div aria-hidden="true" style={{
      position: 'absolute', width: size * 1.45, height: size * 1.45, borderRadius: '50%',
      background: 'radial-gradient(closest-side, rgba(201,168,76,0.30), rgba(201,168,76,0) 70%)',
      transition: 'opacity 0.35s ease', opacity: speaking ? 1 : 0.75,
    }} />
    <img
      src={`/pip/${PIP_POSE[pose] || PIP_POSE.idle}.png`}
      alt="Pip"
      style={{
        height: size, width: size, objectFit: 'contain', position: 'relative',
        filter: 'drop-shadow(0 8px 14px rgba(120,70,20,0.22))',
        transform: speaking ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.3s ease',
      }}
    />
  </div>
);

// ---------------------------------------------------------------------------
// Progress meter tiers — thresholds are in SUBSTANTIVE user words accumulated
// across all user turns during the live call.
//
//   0-29   "Keep going..."     — not yet enough; Done button not enabled
//  30-89   "Okay Story"        — minimum to write it up; Done becomes enabled
//  90-179  "Great Story"       — richer content; celebratory label
// 180+     "Amazing Story!"    — Pip goes delighted; full celebratory flourish
//
// These replace the old 8-word hard eject.
// ---------------------------------------------------------------------------
const TIERS = [
  { min: 0,   max: 29,  label: 'Keep going...',   sublabel: 'Just a bit more and your story is ready!', enabled: false, color: '#b8a88a' },
  { min: 30,  max: 89,  label: 'Okay Story',      sublabel: 'Nice! Ready to write it up.',               enabled: true,  color: '#c9a84c' },
  { min: 90,  max: 179, label: 'Great Story',      sublabel: 'You\'re on a roll. Keep going or write it up now!', enabled: true, color: '#b07d28' },
  { min: 180, max: Infinity, label: 'Amazing Story!', sublabel: 'Pip is loving this. Ready whenever you are!', enabled: true, color: '#e8801a' },
];

function getTier(wordCount) {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (wordCount >= TIERS[i].min) return TIERS[i];
  }
  return TIERS[0];
}

// Count substantive words from a transcript turn (filter single-char noise).
function countSubstantiveWords(text) {
  return (text || '').trim().split(/\s+/).filter(w => w.length > 1).length;
}

// ---------------------------------------------------------------------------
// StoryMeter — animated progress bar + tier label shown during the live call.
// ---------------------------------------------------------------------------
const METER_MAX = 200; // word count at which bar is "full"

const StoryMeter = ({ wordCount }) => {
  const tier = getTier(wordCount);
  const pct = Math.min(100, Math.round((wordCount / METER_MAX) * 100));
  const isAmazing = wordCount >= 180;

  return (
    <div className="vi-meter-wrap" aria-label={`Story progress: ${tier.label}`}>
      <div className="vi-meter-row">
        <span className="vi-meter-tier" style={{ color: tier.color }}>
          {tier.label}
        </span>
        <span className="vi-meter-words">{wordCount} {wordCount === 1 ? 'word' : 'words'}</span>
      </div>
      <div className="vi-meter-track" role="progressbar" aria-valuenow={wordCount} aria-valuemin={0} aria-valuemax={METER_MAX}>
        <div
          className={`vi-meter-fill${isAmazing ? ' vi-meter-fill-amazing' : ''}`}
          style={{ width: `${pct}%`, background: tier.color }}
        />
        {/* Tick marks at tier boundaries */}
        <div className="vi-meter-tick" style={{ left: `${(30 / METER_MAX) * 100}%` }} aria-hidden="true" />
        <div className="vi-meter-tick" style={{ left: `${(90 / METER_MAX) * 100}%` }} aria-hidden="true" />
        <div className="vi-meter-tick" style={{ left: `${(180 / METER_MAX) * 100}%` }} aria-hidden="true" />
      </div>
      <div className="vi-meter-sub" style={{ color: tier.color }}>
        {tier.sublabel}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// VoiceInterview — the full voice interview surface.
//
// Props:
//   selectedPrompt     { text, emoji } — the interview question
//   onDone(transcript, conversationId) — called when user taps "Done"; receives
//                        [{role,content}] plus the ElevenLabs conversation id (for
//                        fetching the full server-side recording → QR).
//   onBack()           — user exits before starting
//   isGiftBook         — boolean for framing copy
//   recipientName      — string, for gift-book framing
//   pack               — string, the active topic pack id (e.g. "trouble-i-got-into"),
//                        matching a key in interviewEngine.js's PACK_CONFIG. TB-134
//                        pack-wiring fix: threaded through to the ElevenLabs session via
//                        customLlmExtraBody so the voice adapter's engine-driven tone and
//                        move selection matches the actual chosen topic instead of always
//                        defaulting to the romance pack. An empty/unrecognized value is
//                        handled safely by the adapter (falls back to its own default pack).
// ---------------------------------------------------------------------------
const VoiceInterviewInner = ({
  selectedPrompt,
  onDone,
  onBack,
  onCancel,
  onSwitchToText,
  seedless = false, // F-83: seedless entry -> the idle Back reads "Back" and returns to the doorway
  isGiftBook = false,
  recipientName = '',
  occasionId = '', // F-58: memorial (IN_MEMORY) greeting swaps "special for" → "in memory of"
  pack = '',
  savedStoryCount = 0, // F-24 opener graduation
  autoStart = false, // typed "Talk to Pip instead" arms a turn; skip the Ready replay
}) => {
  // Session lifecycle
  const [phase, setPhase] = React.useState('idle'); // idle | fetching | connected | error | done
  const [errorMsg, setErrorMsg] = React.useState('');
  // Bilingual: storyteller picks English or Spanish before starting. Pip already follows the
  // user into Spanish mid-call; this lets them OPEN in Spanish (discoverable), persisted so the
  // choice sticks. Full Spanish-first launch (landing + prompt packs + text surface) = TB-114.
  const [lang, setLang] = React.useState(() => {
    try { return localStorage.getItem('tb_lang') === 'es' ? 'es' : 'en'; } catch (e) { return 'en'; }
  });
  const [langOpen, setLangOpen] = React.useState(false);
  const [micHint, setMicHint] = React.useState('');
  const chooseLang = React.useCallback((next) => {
    setLang(next);
    try { localStorage.setItem('tb_lang', next); } catch (e) {}
    setLangOpen(false);
  }, []);
  // phaseRef: used inside ElevenLabs callbacks to avoid stale closure reads.
  // The hook memoises the callbacks so they cannot reliably read React state directly.
  const phaseRef = React.useRef('idle');
  const setPhaseTracked = React.useCallback((next) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // Transcript accumulation
  const [transcript, setTranscript] = React.useState([]); // [{role:'user'|'ai', content}]
  const transcriptRef = React.useRef([]);
  // Conversation id — captured while connected so we can fetch the authoritative
  // server-side transcript after the call (getId() may be null after endSession).
  const conversationIdRef = React.useRef(null);

  // Scroll anchor
  const endRef = React.useRef(null);

  // F-108: reserve scroll space equal to the REAL rendered dock height (measured, never hardcoded) so the
  // newest transcript bubble is never occluded behind the fixed dock. The dock grows with the below-floor
  // nudge + the helper line + the safe-area inset, so measure it live via ResizeObserver.
  const dockRef = React.useRef(null);
  const [dockHeight, setDockHeight] = React.useState(160);
  React.useLayoutEffect(() => {
    const el = dockRef.current;
    if (!el) return;
    const measure = () => setDockHeight(el.offsetHeight || 160);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Track whether the user has spoken at all yet — used to show a one-time
  // extra-prominent "Go ahead, I'm listening" cue on the very first turn.
  const [hasSpokenOnce, setHasSpokenOnce] = React.useState(false);

  // Live accumulated word count across all user turns. Updated on every onMessage
  // so the meter fills in real time as the storyteller speaks.
  const [userWordCount, setUserWordCount] = React.useState(0);
  const userWordCountRef = React.useRef(0);

  // Whether the nudge toast has been shown for the current "early Done" tap
  // (prevents repeated nudges if the user taps Done multiple times before the threshold).
  const nudgeShownRef = React.useRef(false);

  // Interview-length guardrails: a gentle, non-blocking invitation to wrap up at the
  // soft point. Generous on purpose ("accessible and magic" first; cost backstop second).
  const [showLengthNudge, setShowLengthNudge] = React.useState(false);
  // Arms the two-tap "finish anyway" exit when the story is below the meter threshold,
  // so a short interview is never a dead end. Reset whenever new words come in.
  const [shortFinishArmed, setShortFinishArmed] = React.useState(false);

  // Inline message shown when the user taps Done but hasn't said enough yet.
  // Keeps the session live so they can keep talking instead of exiting.
  const [emptyWriteupMsg, setEmptyWriteupMsg] = React.useState('');

  // Exit-confirm overlay: shown when the user taps Cancel/Exit with real content spoken.
  const [exitConfirm, setExitConfirm] = React.useState(false);

  // PIPE-SAVE-1: arms the spoken-done-signal auto-finish exactly once per session, and holds the
  // pending finish timer so it can be cancelled on a manual finish or on unmount.
  const doneSignalArmedRef = React.useRef(false);
  const doneSignalTimerRef = React.useRef(null);
  // F-263 (founder ruling R1): wall-clock of the last user turn that carried REAL content. The 5-minute
  // silence backstop measures from here. Set on connect and on every non-silent user transcript event.
  // WHY WALL-CLOCK AND NOT AN EVENT COUNT: once the adapter emits skip_turn, ElevenLabs stops forwarding
  // silent turns entirely, so the adapter goes blind to the silence it just handled. A clock only needs
  // events to RESET, never to COUNT, which is precisely what lets this survive that blindness.
  const lastRealUserTurnAtRef = React.useRef(null);
  // F-91: the pre-flight getUserMedia stream from handleStart. It must be track.stop()'d on every exit
  // or the browser keeps the mic "in use" after the user leaves (endSession only releases the SDK's own
  // WebRTC mic, not this permission-probe stream) — a trust/privacy + lingering-mic bug.
  const micStreamRef = React.useRef(null);
  // F-75: wall-clock anchor for the graceful-landing schedule (set once on first connect; never reset).
  const sessionStartRef = React.useRef(null);

  // ElevenLabs hook
  const conversation = useConversation({
    onConnect: () => {
      // F-75: anchor the graceful-landing schedule to the FIRST connect (wall-clock), so a mid-session
      // reconnect never resets it (the F-71 bug where the 28-min timer restarted on every reconnect and
      // so never fired on a marathon mobile session). Set once per mount.
      if (sessionStartRef.current == null) sessionStartRef.current = Date.now();
      setPhaseTracked('connected');
    },
    onDisconnect: () => {
      // If we had a live session, move to done so the user can write it up.
      // Use phaseRef to avoid stale closure (callbacks are memoised by the SDK).
      if (phaseRef.current === 'connected') {
        // F-75 silent net (Part 2, Build 1): if the teller GAVE a done-signal (armed) but the 3.5s
        // finish had not fired yet, honor it on the drop — run finishUp so her story still completes as
        // an unsaved draft (REVEAL-002 R2), instead of the F-73 race where the delayed finish no-ops on
        // 'done' and nothing generates. A user-given done-signal is consent, so grief does NOT block it.
        if (doneSignalArmedRef.current) {
          if (doneSignalTimerRef.current) { clearTimeout(doneSignalTimerRef.current); doneSignalTimerRef.current = null; }
          finishUpRef.current?.({ force: true, viaDoneSignal: true, trigger: 'spoken' });
          return;
        }
        setPhaseTracked('done');
      }
    },
    onMessage: ({ message, source }) => {
      if (!message || !message.trim()) return;
      const role = source === 'user' ? 'user' : 'ai';
      const entry = { role, content: message.trim() };
      transcriptRef.current = [...transcriptRef.current, entry];
      setTranscript(prev => [...prev, entry]);
      // F-263: the 5-minute silence backstop resets on REAL user content only. A silent turn (the
      // ElevenLabs ellipsis placeholder) must NOT reset it, or a teller who has walked away holds the
      // line open to the 30-minute cap. Uses the SHARED engine predicate — the same one the adapter's
      // silence stop runs — so the two can never disagree about what silence is (§3).
      if (role === 'user' && !isSilentTurn(entry.content)) {
        lastRealUserTurnAtRef.current = Date.now();
      }
      // Arm the SAME finishUp the Done button uses on a spoken done-signal. `source` is 'floor' (trimmed
      // deterministic fast-path on the USER turn) or 'sentinel' (Pip spoke the DONE_SIGNAL_CLOSE_LINE
      // because the model called save_story OR the floor stopped). Armed once (BN2): whichever fires
      // first wins; the other is a no-op. A short delay lets Pip's intent-only close play first.
      // REVEAL-002 R2: this finish now routes to a DRAFT + State 0 (no save) — trigger:'spoken'.
      const armDoneFinish = (source) => {
        if (doneSignalArmedRef.current) return; // BN2: no double-fire
        doneSignalArmedRef.current = true;
        try { trackEvent('pipe_save_trigger', { source, acted: true }); } catch (e) { /* analytics never breaks flow; event name kept for the trim-validation measurement pipeline (pipeline.md DZ-12) */ }
        doneSignalTimerRef.current = setTimeout(() => {
          if (phaseRef.current === 'connected') finishUpRef.current?.({ force: true, viaDoneSignal: true, trigger: 'spoken' });
        }, DONE_SIGNAL_FINISH_DELAY_MS);
      };

      if (role === 'user') {
        setHasSpokenOnce(true);
        // Update the live word count so the meter reacts immediately.
        const added = countSubstantiveWords(message);
        userWordCountRef.current = userWordCountRef.current + added;
        setUserWordCount(userWordCountRef.current);
        nudgeShownRef.current = false; // reset nudge so it can fire again on next early tap
        setShortFinishArmed(false); // they kept talking — re-arm the gentle nudge before any early finish

        if (doneSignalArmedRef.current) {
          // FINISH-INTENT GUARD (REVEAL-002 R2): a new user utterance arrived during the 3.5s grace
          // window. Cancel the pending finish unless it is a confirmation — never end a story early. This
          // is no longer a "save" guard (a finish saves nothing now); it exists because a premature finish
          // is currently one-way while TELL_PIP_MORE_ENABLED is false (no reopen). PUNCH-LIST: revisit —
          // shrink or remove this window — once PIPE-APPEND un-flags the "Tell Pip more" door (reopen makes
          // a premature finish recoverable, so the guard's rationale weakens).
          if (shouldCancelPendingFinish(message, added)) {
            if (doneSignalTimerRef.current) { clearTimeout(doneSignalTimerRef.current); doneSignalTimerRef.current = null; }
            doneSignalArmedRef.current = false;
            try { trackEvent('pipe_save_objection', { via: 'user_utterance' }); } catch (e) { /* never break flow; event name kept for measurement continuity */ }
          }
          // a confirmation falls through and lets the pending finish fire.
        } else if (phaseRef.current === 'connected') {
          // Tier-1 TRIMMED FLOOR fast-path (source 'floor').
          let isDoneSignal = false;
          try { isDoneSignal = classifyAnswer(message, {}).label === 'DONE-SIGNAL'; } catch (e) { isDoneSignal = false; }
          if (isDoneSignal) {
            armDoneFinish('floor');
          } else {
            // D1 shadow rider: the WIDE (pre-trim) floor would have fired but the trimmed floor did
            // not. Log-only — never acts. Measures what the trim drops (the model tool carries recall).
            try { if (hasDoneSignalWide(message)) trackEvent('pipe_save_trigger', { source: 'shadow-floor-would-have-fired', acted: false }); } catch (e) { /* never break flow */ }
          }
        }
      } else if (!doneSignalArmedRef.current && phaseRef.current === 'connected' && matchesSaveCloseSentinel(message)) {
        // role === 'ai' BN1 SENTINEL: Pip spoke the fixed close line = the authoritative save election
        // relayed via the transcript (the only adapter->frontend channel). Route into the same seam.
        armDoneFinish('sentinel');
      }
      /* VOICE-READY-SHELL-1: thread pane owns scroll (applyThreadScroll). */
    },
    onError: (err) => {
      console.error('[VoiceInterview] Session error:', err);
      setPhaseTracked('error');
      setErrorMsg('Something went wrong with the voice connection. You can try again.');
    },
  });

  const isSpeaking = conversation.isSpeaking;
  const sessionStatus = conversation.status; // 'connected' | 'connecting' | 'disconnected'

  // F-91: a live ref to the SDK conversation so the []-deps unmount cleanup can end the session without
  // a stale closure. Updated every render.
  const conversationRef = React.useRef(conversation);
  conversationRef.current = conversation;
  // F-91: full mic + session release, safe to call repeatedly. endSession closes the ElevenLabs
  // conversation (releasing the SDK's WebRTC mic); stopping every track of the pre-flight stream
  // releases the permission-probe mic. Both together guarantee the browser mic indicator turns off.
  const releaseSession = React.useCallback(() => {
    try { conversationRef.current?.endSession?.(); } catch (e) { /* already ended */ }
    try { micStreamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (e) { /* no stream */ }
    micStreamRef.current = null;
  }, []);

  // Capture the conversation id while connected (getId() may return null after endSession).
  React.useEffect(() => {
    if (sessionStatus === 'connected') {
      try {
        const id = conversation.getId && conversation.getId();
        if (id) conversationIdRef.current = id;
      } catch (e) { /* getId not ready yet */ }
    }
  }, [sessionStatus, conversation]);

  // Derived display state
  const isConnected = sessionStatus === 'connected';
  const isConnecting = sessionStatus === 'connecting' || phase === 'fetching';
  const preCall = isVoicePreCallPhase(phase, { isConnecting });

  // Current tier — drives the meter and the Done button gate.
  const currentTier = getTier(userWordCount);
  const doneEnabled = currentTier.enabled && !isConnecting;
  const isAmazingTier = userWordCount >= 180;

  // Which Pip pose to show, driven by the live call state.
  // Amazing Story tier while connected: Pip goes delighted as a celebration flourish.
  const pipPose =
    phase === 'writingup' ? 'thinking' :
    phase === 'done' ? 'delighted' :
    (isConnected && isAmazingTier && !isSpeaking) ? 'delighted' :
    (isConnected && isSpeaking) ? 'speaking' :
    isConnected ? 'listening' :
    'greet';

  // ---------------------------------------------------------------------------
  // Start session
  // ---------------------------------------------------------------------------
  const handleStart = async () => {
    setPhaseTracked('fetching');
    setErrorMsg('');
    try {
      // Mic permission — request explicitly so Safari shows a clear permission dialog
      // before we attempt WebRTC. This surfaces a native prompt, not a cold failure.
      // F-91: KEEP the stream so we can stop its tracks on exit (see releaseMic). A discarded stream
      // leaks the mic until GC — the "mic stays on after Cancel" bug.
      micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (micErr) {
      setPhaseTracked('error');
      setErrorMsg(
        'We need microphone access to start the voice interview. ' +
        'Please allow microphone access in your browser settings and try again.'
      );
      return;
    }

    let token;
    try {
      token = await fetchVoiceToken(selectedPrompt?.text || '');
    } catch (tokenErr) {
      // Graceful gate: token endpoint may not be live yet
      if (
        tokenErr.message.includes('404') ||
        tokenErr.message.includes('No conversation token') ||
        tokenErr.message.includes('AI request failed')
      ) {
        setPhaseTracked('error');
        setErrorMsg(
          'The voice interview is being set up and will be available very soon. ' +
          'In the meantime, you can use the written interview below.'
        );
      } else if (
        tokenErr.message.includes('Authentication') ||
        tokenErr.message.includes('Sign-in')
      ) {
        setPhaseTracked('error');
        setErrorMsg('Please sign in to use the voice interview.');
      } else if (tokenErr.code === 'USER_LIMIT') {
        setPhaseTracked('error');
        setErrorMsg(tokenErr.message || "You've reached today's voice session limit.");
      } else {
        setPhaseTracked('error');
        setErrorMsg('Could not start the voice session. Please try again in a moment.');
      }
      return;
    }

    // The agent's "First message" override is now enabled in the ElevenLabs dashboard,
    // so Pip opens with the EXACT prompt the storyteller picked instead of the agent's
    // hardcoded default. (This requires the override toggle ON + the agent published;
    // with it off, ElevenLabs accepts the connection then kills it.)
    const opener = buildVoiceOpener(selectedPrompt, isGiftBook, recipientName, lang, savedStoryCount, occasionId);
    // TB-134 pack-wiring fix: pass the active topic pack through ElevenLabs' customLlmExtraBody,
    // which ElevenLabs merges into the OpenAI-style request body it POSTs to the custom-LLM
    // Lambda (remnantVoiceAdapter) on every turn — arrives there as body.pack (confirmed against
    // @elevenlabs/client's overrides.js and ElevenLabs' custom-LLM docs). The adapter validates
    // this against its own known pack list and falls back safely if it's missing or unrecognized,
    // so an empty/unknown pack here never breaks the session.
    // F-62 (Front Door wiring): coldStart is CALLER state — true only on a seedless entry (no picked
    // question). ElevenLabs merges customLlmExtraBody into every turn's POST to the voice adapter, so
    // the adapter reads it as body.coldStart (same seam as pack). Inert today: every current entry
    // path arrives with a picked prompt; the doorway (separate Tier-A track) is what makes this true.
    const isSeedlessEntry = !((selectedPrompt?.text || '').trim());
    const extraBody = {
      ...((pack && pack.trim()) ? { pack: pack.trim() } : {}),
      ...(isSeedlessEntry ? { coldStart: true } : {}),
    };
    try {
      // startSession is fire-and-forget in v1.6.7 — await it but don't block on return value
      await conversation.startSession({
        conversationToken: token,
        connectionType: 'webrtc',
        ...(opener ? { overrides: { agent: { firstMessage: opener } } } : {}),
        ...(Object.keys(extraBody).length ? { customLlmExtraBody: extraBody } : {}),
      });
      // onConnect will fire and move phase to 'connected'
    } catch (sessionErr) {
      console.error('[VoiceInterview] startSession error:', sessionErr);
      setPhaseTracked('error');
      setErrorMsg('Could not connect. Please check your connection and try again.');
    }
  };
  const handleStartRef = React.useRef(handleStart);
  handleStartRef.current = handleStart;

  const handleReturnToReady = () => {
    releaseSession();
    setErrorMsg('');
    setPhaseTracked('idle');
  };

  const autoStartedRef = React.useRef(false);
  React.useEffect(() => {
    if (autoStart && !autoStartedRef.current) {
      autoStartedRef.current = true;
      handleStartRef.current?.();
    }
  }, [autoStart]);

  React.useEffect(() => {
    if (typeof Image === 'undefined') return;
    Object.values(PIP_POSE).forEach((name) => {
      const img = new Image();
      img.src = `/pip/${name}.png`;
    });
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const hasGetUserMedia = !!(typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      let permissionState = 'unknown';
      let audioInputCount = null;
      try {
        if (typeof navigator !== 'undefined' && navigator.permissions && navigator.permissions.query) {
          const p = await navigator.permissions.query({ name: 'microphone' });
          permissionState = p.state;
        }
      } catch (e) { /* Safari / denied-by-policy */ }
      try {
        if (typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          audioInputCount = devices.filter((d) => d.kind === 'audioinput').length;
        }
      } catch (e) { /* ignore */ }
      if (cancelled) return;
      const result = classifyMicPreflight({ hasGetUserMedia, permissionState, audioInputCount });
      setMicHint(result.hint || '');
    })();
    return () => { cancelled = true; };
  }, []);

  const connectingStartedAtRef = React.useRef(null);
  React.useEffect(() => {
    if (!(isConnecting && phase !== 'connected')) {
      connectingStartedAtRef.current = null;
      return undefined;
    }
    if (connectingStartedAtRef.current == null) connectingStartedAtRef.current = Date.now();
    const id = setTimeout(() => {
      if (phaseRef.current === 'connected') return;
      if (!isVoiceConnectingTimeout({ startedAt: connectingStartedAtRef.current, now: Date.now() })) return;
      releaseSession();
      setPhaseTracked('error');
      setErrorMsg('Could not start the voice session. Please try again in a moment.');
    }, VOICE_READY.connectingTimeoutMs);
    return () => clearTimeout(id);
  }, [isConnecting, phase, releaseSession, setPhaseTracked]);

  const shellRef = React.useRef(null);
  const threadRef = React.useRef(null);
  React.useEffect(() => {
    if (preCall) return undefined;
    const id = setTimeout(() => {
      const thread = threadRef.current;
      const latest = thread && thread.querySelector('[data-vi-latest-pip="true"]');
      applyThreadScroll(thread, latest);
    }, 50);
    return () => clearTimeout(id);
  }, [transcript, phase, preCall, emptyWriteupMsg, showLengthNudge]);
  React.useEffect(() => {
    if (!shellRef.current) return undefined;
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('iv-chat-lock');
    body.classList.add('iv-chat-lock');
    const apply = () => {
      const shell = shellRef.current;
      if (!shell || typeof window === 'undefined') return;
      const vv = window.visualViewport;
      const mobile = window.innerWidth <= 640;
      const insets = typedShellInsets({
        innerHeight: window.innerHeight,
        visualViewportHeight: vv ? vv.height : window.innerHeight,
        visualViewportOffsetTop: vv ? vv.offsetTop : 0,
        mobile,
        innerWidth: window.innerWidth,
      });
      if (mobile) {
        shell.style.top = `${(vv ? vv.offsetTop : 0) + insets.top}px`;
        shell.style.left = '0px';
        shell.style.right = '0px';
        shell.style.bottom = `${insets.bottom}px`;
        shell.style.height = 'auto';
      } else {
        shell.style.top = '0px';
        shell.style.left = `${insets.left || desktopSidebarPx(window.innerWidth)}px`;
        shell.style.right = '0px';
        shell.style.bottom = '0px';
        shell.style.height = 'auto';
      }
    };
    apply();
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (vv) {
      vv.addEventListener('resize', apply);
      vv.addEventListener('scroll', apply);
    }
    window.addEventListener('resize', apply);
    return () => {
      root.classList.remove('iv-chat-lock');
      body.classList.remove('iv-chat-lock');
      if (vv) {
        vv.removeEventListener('resize', apply);
        vv.removeEventListener('scroll', apply);
      }
      window.removeEventListener('resize', apply);
    };
  }, [preCall]);

  // ---------------------------------------------------------------------------
  // End session and hand off to write-up pipeline.
  // Belt-and-suspenders: if the user somehow reaches this with insufficient content
  // (meter gate should prevent it), stay live and nudge rather than ejecting.
  //
  // RACE FIX (TB-104): ElevenLabs finalizes the server-side transcript ASYNCHRONOUSLY
  // after endSession(). Fetching it immediately returns an incomplete stub (often just
  // the first turn), which then overwrites the richer live transcript. The fix:
  //   1. Show 'writingup' immediately so the user sees reassuring feedback.
  //   2. Poll fetchVoiceTranscript with retries (every 1.5s, up to ~15s).
  //   3. Keep the BEST result seen across polls AND vs transcriptRef (max user turns /
  //      max total words). A shorter server result NEVER overwrites a richer one.
  //   4. Stabilize: stop when turn count is stable across 2 consecutive polls.
  //   5. Timeout gracefully: use the best transcript seen, not just the last.
  //
  // onMessage note: the live stream populates transcriptRef with both 'user' and 'ai'
  // turns as they arrive, but the user's transcribed speech may be incomplete mid-sentence.
  // The server record has the authoritative full transcription. Both are kept and the
  // richer of the two is used.
  // ---------------------------------------------------------------------------
  const finishUp = async ({ force = false, viaDoneSignal = false, trigger = null } = {}) => {
    // PIPE-SAVE-1: cancel any pending spoken-done-signal auto-finish timer (a manual finish, or this
    // very timer firing, must not leave a second finish armed).
    if (doneSignalTimerRef.current) { clearTimeout(doneSignalTimerRef.current); doneSignalTimerRef.current = null; }
    // Empty guard: count user turns before committing to writingup.
    // We must do this BEFORE setPhaseTracked('writingup') so we never
    // enter the loading screen on content that cannot be written up.
    const userTurnsInRef = transcriptRef.current.filter(t => t.role === 'user');
    const userWordCountInRef = userTurnsInRef.reduce(
      (sum, t) => sum + countSubstantiveWords(t.content), 0
    );

    if (userTurnsInRef.length === 0) {
      // Nothing said at all. Cancel cleanly — no warning needed.
      if (onCancel) { onCancel(); } else { onBack(); }
      return;
    }

    if (!force && userWordCountInRef < 8) {
      // Something was said, but too thin to write up (e.g. a greeting only).
      // If the session is still live (connected), stay and prompt for more.
      // If the session has already dropped (done phase), cancel cleanly — the user
      // cannot add more content, so the message "say more" would be a dead end.
      if (phaseRef.current === 'connected') {
        setEmptyWriteupMsg("There's nothing to write up yet. Say a little more and then tap Done.");
      } else {
        if (onCancel) { onCancel(); } else { onBack(); }
      }
      return;
    }

    // The meter encourages more below the threshold, but the user must ALWAYS have a way
    // out: the Done button arms a two-tap "finish anyway" (force=true), and the 20-min
    // auto-finish also forces. Only a non-forced call under the minimum is a no-op.
    if (!force && !getTier(userWordCountRef.current).enabled) {
      return;
    }

    setEmptyWriteupMsg('');
    setPhaseTracked('writingup');
    try {
      await conversation.endSession();
    } catch (e) {
      // Ignore end errors — we still want to proceed to write-up
    }

    // Pull the authoritative transcript from ElevenLabs' server record with polling.
    // The live client stream populates transcriptRef but may miss user speech mid-sentence.
    // The server record has the full Whisper-transcribed user turns — essential for the assembler.
    const liveTurns = transcriptRef.current;
    const convId = conversationIdRef.current;

    // Helper: count user words in a turn array
    const totalUserWords = (turns) =>
      (turns || []).filter(t => t.role === 'user').reduce((sum, t) => sum + countSubstantiveWords(t.content), 0);
    const userTurnCount = (turns) =>
      (turns || []).filter(t => t.role === 'user').length;

    // Start with the live client transcript as the current best
    let bestTurns = liveTurns;

    if (convId) {
      const POLL_MAX = 10;       // up to 10 polls
      const POLL_INTERVAL = 1500; // 1.5s between polls → max ~15s
      let prevUserTurnCount = -1;
      let stableRounds = 0;

      for (let i = 0; i < POLL_MAX; i++) {
        // Wait before re-polling (first iteration waits too, giving the server time to settle)
        await new Promise(r => setTimeout(r, POLL_INTERVAL));

        let serverTurns = null;
        try {
          serverTurns = await fetchVoiceTranscript(convId);
        } catch (e) {
          console.error('[VoiceInterview] transcript poll error:', e);
        }

        if (serverTurns && serverTurns.length > 0) {
          // Keep the richer result: prefer more user turns, then more total words
          const serverUserTurns = userTurnCount(serverTurns);
          const bestUserTurns = userTurnCount(bestTurns);
          const serverWords = totalUserWords(serverTurns);
          const bestWords = totalUserWords(bestTurns);
          if (
            serverUserTurns > bestUserTurns ||
            (serverUserTurns === bestUserTurns && serverWords > bestWords)
          ) {
            bestTurns = serverTurns;
          }

          // Stabilization: stop when user turn count stops growing
          if (serverUserTurns > 0 && serverUserTurns === prevUserTurnCount) {
            stableRounds += 1;
            if (stableRounds >= 2) break; // stable for 2 rounds — done
          } else {
            stableRounds = 0;
          }
          prevUserTurnCount = serverUserTurns;
        }
      }
    }

    // Pass the conversation id alongside the turns so the save flow can fetch the
    // full server-side recording (interviewer + storyteller) and QR-link it.
    // REVEAL-002 R2 (UNIFIED-FINISH): a finish saves NOTHING. viaDoneSignal + trigger tell the write-up
    // flow HOW this finish arrived ('spoken' close-line, 'timer' 28-min cap, or 'button' tap) so
    // story_ready_viewed can record it. All three route to the same draft + State 0 — no auto-save.
    onDone(bestTurns, convId, { viaDoneSignal, trigger: trigger || (viaDoneSignal ? 'spoken' : 'button') });
  };

  // Keep a live ref to finishUp so the auto-finish timer never fires a stale closure.
  const finishUpRef = React.useRef(null);
  finishUpRef.current = finishUp;

  const handleFinishTap = () => {
    if (isConnecting) return;
    if (doneEnabled) { finishUp(); return; }
    if (!shortFinishArmed) {
      setShortFinishArmed(true);
      setTranscript((prev) => [...prev, { role: 'ai', content: BELOW_FLOOR_NUDGE }]);
      return;
    }
    finishUp({ force: true });
  };

  // F-91: GUARANTEED full release on unmount — the single point EVERY leave vector passes through
  // (Cancel/Leave, seedless Back -> doorway, topic-picked cancel, finish, or any future exit): the
  // component always unmounts when stVoiceInterviewMode flips false. End the session, stop the mic
  // tracks, and clear the done-signal timer here so no path can leave the mic live. (The F-75
  // wind-down timers are already cleared by their own effect's cleanup on unmount.)
  React.useEffect(() => () => {
    if (doneSignalTimerRef.current) clearTimeout(doneSignalTimerRef.current);
    releaseSession();
  }, [releaseSession]);

  // ---------------------------------------------------------------------------
  // handleExitLive — Cancel/Exit during a live or dropped session.
  // If nothing was said, disconnect and exit immediately (no confirmation).
  // If the user said something, show the exitConfirm overlay first.
  // ---------------------------------------------------------------------------
  const handleExitLive = () => {
    if (userWordCountRef.current === 0) {
      releaseSession(); // F-91: end session + stop mic immediately (unmount also releases as a backstop)
      if (onCancel) { onCancel(); } else { onBack(); }
    } else {
      setExitConfirm(true);
    }
  };

  // F-75 GRACEFUL TIME-LIMIT LANDING. A live voice session has a hard cap (the ElevenLabs agent's
  // max_duration_seconds — 1800s / 30 min). If we let it hit the cap, ElevenLabs disconnects
  // mid-sentence and the teller lands on the silent 'done' card (or, if she has walked away, on
  // nothing) — an abrupt cutoff that loses a complete story (F-71/F-73: Person B ended at 27.6 min,
  // saved nothing). This schedules a graceful finish with MARGIN so finishUp() runs (self-disconnect +
  // client-side generation) and she lands on the REVEAL before the cap.
  //
  // vs the old 28-min timer this fixes three things:
  //   1. MARGIN — finish at cap-150s (27:30), so generation reliably beats the hard cut. 27:30 also
  //      CATCHES a ~27.6-min natural-ender like Person B (~8s before she stops → she lands on the reveal).
  //   2. RECONNECT-ROBUST — the schedule is anchored to sessionStartRef (first connect wall-clock), so a
  //      mid-session reconnect no longer RESETS the clock (the F-71 bug where the timer restarted on every
  //      isConnected flip and so never fired on a marathon mobile session). We re-arm on each connect but
  //      compute the REMAINING time from the anchor.
  //   3. DIGNITY — grief/crisis conversations are EXCLUDED from the auto-finish (never hurry a grieving
  //      teller toward a wrap on a timer). A user-GIVEN done-signal is separate consent and still finishes.
  // Build 2 (flagged): Pip's SPOKEN preservation wind-down at the heads-up beat — that must come from the
  // voice ADAPTER (the client cannot synthesize Pip's TTS) and needs live-device smoke, so it is not here.
  // The on-screen nudge below is retained but is eyes-off-useless on voice; the spoken beat is the real fix.
  React.useEffect(() => {
    if (!isConnected || sessionStartRef.current == null) return undefined;
    const { headsUpAtSec, finishAtSec } = voiceWindDownSchedule(); // default cap 1800s
    const elapsedMs = () => Date.now() - sessionStartRef.current;
    const remaining = (atSec) => Math.max(0, atSec * 1000 - elapsedMs()); // ms until this beat, from the anchor
    const softTimer = setTimeout(() => setShowLengthNudge(true), remaining(headsUpAtSec));
    const hardTimer = setTimeout(() => {
      if (phaseRef.current !== 'connected') return; // guard against a double finish after a manual/spoken Done
      // DIGNITY GATE: never auto-finish a grief/crisis conversation on a timer. Let it run to the cap
      // ('done' card), where the silent net still honors any done-signal she gives.
      if (isGriefOrCrisisConversation(transcriptRef.current)) {
        try { trackEvent('voice_winddown_skipped_grief', {}); } catch (e) { /* never break flow */ }
        return;
      }
      try { trackEvent('voice_winddown_autofinish', { trigger: 'timer' }); } catch (e) { /* never break flow */ }
      finishUpRef.current?.({ force: true, trigger: 'timer' });
    }, remaining(finishAtSec));
    return () => { clearTimeout(softTimer); clearTimeout(hardTimer); };
    // Depend on `phase` too: onConnect sets the sessionStart anchor AND flips phase to 'connected',
    // so re-running here arms the timers once the anchor exists. Re-arming on later phase changes is
    // idempotent — the beats are anchored to sessionStart wall-clock via remaining(), not restarted.
  }, [isConnected, phase]);

  // ---------------------------------------------------------------------------
  // F-263 — THE 5-MINUTE SILENCE BACKSTOP (founder ruling R1).
  //
  // WHAT IT IS FOR. The adapter says its one gentle line and then goes quiet (skip_turn). That is the
  // right thing to say to a teller who paused. It is the WRONG thing to do forever for a teller who put
  // the phone down and walked away: with silence_end_call_timeout = -1 the vendor never ends the call, so
  // the session would hold open to the 30-minute cap, billing the whole time, and the teller would come
  // back to nothing — the F-71/F-73 loss by a slower route. So at 5 minutes of no real content WE end it,
  // gracefully: the SAME finishUp() the 27:30 landing uses, which generates client-side and lands the
  // teller on the REVEAL with an unsaved draft waiting. Never a bare vendor hang-up.
  //
  // WHY A SECOND TIMER RATHER THAN FOLDING IT INTO THE F-75 EFFECT: they are anchored to different
  // clocks. F-75 measures from session start and never resets (that immutability IS the F-71 fix); this
  // one measures from the last real answer and resets constantly. Sharing an effect would mean one of the
  // two anchors moving, which is exactly the bug F-71 was.
  //
  // DIGNITY GATE, unchanged and consulted at the same decision point as the 27:30 landing: a grief or
  // crisis conversation is NEVER auto-finished on a timer. A crying teller can read as silent to a
  // transcript-only predicate (see the crying test in docs/WALKS/), so this gate is load-bearing here in
  // a way it is not at 27:30. F-263 does not amend it.
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    if (!isConnected) return undefined;
    if (lastRealUserTurnAtRef.current == null) lastRealUserTurnAtRef.current = Date.now();
    const TICK_MS = 5000;
    const id = setInterval(() => {
      if (phaseRef.current !== 'connected') return; // a manual/spoken finish already won
      const since = Date.now() - (lastRealUserTurnAtRef.current || Date.now());
      if (since < VOICE_SILENCE_FINISH_SEC * 1000) return;
      if (isGriefOrCrisisConversation(transcriptRef.current)) {
        try { trackEvent('voice_silence_finish_skipped_grief', {}); } catch (e) { /* never break flow */ }
        return;
      }
      try { trackEvent('voice_silence_autofinish', { trigger: 'silence', afterSec: Math.round(since / 1000) }); } catch (e) { /* never break flow */ }
      finishUpRef.current?.({ force: true, trigger: 'silence' });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [isConnected, phase]);

  // ---------------------------------------------------------------------------
  // Status line — what to show below the orb
  // ---------------------------------------------------------------------------
  const getStatusLine = () => {
    if (isConnecting) return '';
    if (!isConnected && phase !== 'done') return '';
    if (isSpeaking) return 'Speaking...';
    return 'Listening';
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Embedded .vi-* styles — extend the .iv-* palette without conflicting */}
      <style>{`
        html.iv-chat-lock, body.iv-chat-lock { overflow: hidden; height: 100%; overscroll-behavior: none; }
        .vi-chat-shell {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: #fbf8f1;
          max-width: 520px;
          margin: 0 auto;
          height: calc(100svh - 80px);
        }
        @media (max-width: 640px) {
          .vi-chat-shell {
            position: fixed;
            top: 48px;
            left: 0;
            right: 0;
            bottom: calc(64px + env(safe-area-inset-bottom, 0px));
            max-width: none;
            margin: 0;
            height: auto;
            z-index: 90;
          }
        }
        @media (min-width: 641px) {
          .vi-chat-shell {
            position: fixed;
            top: 0;
            left: 280px;
            right: 0;
            bottom: 0;
            max-width: none;
            margin: 0;
            height: auto;
            z-index: 90;
          }
        }
        @media (min-width: 641px) and (max-width: 1024px) {
          .vi-chat-shell { left: 200px; }
        }
        .vi-chat-header {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 4px 10px;
          background: #efebe0;
          border-bottom: 1px solid rgba(15,23,42,0.10);
        }
        .vi-header-btn {
          min-height: 44px;
          padding: 8px 12px;
          background: none;
          border: none;
          cursor: pointer;
          font-family: 'Nunito', sans-serif;
          font-size: 0.92rem;
          font-weight: 700;
          color: #5c4f38;
          -webkit-tap-highlight-color: transparent;
        }
        /* Desktop: exact live .sidebar-brand offsetHeight (59px) so hairlines meet. */
        @media (min-width: 641px) {
          .vi-chat-header {
            height: 59px;
            box-sizing: border-box;
            padding: 0 24px;
            display: flex;
            align-items: center;
          }
          .vi-header-btn,
          .vi-lang-chip,
          .vi-finish-header {
            min-height: 0;
            padding-top: 0;
            padding-bottom: 0;
            display: inline-flex;
            align-items: center;
          }
        }
        .vi-thread {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          padding: 12px 16px 18px;
        }
        .vi-ready-dock {
          flex: 0 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 10px;
          background: #efebe0;
          border-top: 1px solid rgba(15,23,42,0.10);
          padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
        }
        .vi-live-dock {
          flex: 0 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          background: #efebe0;
          border-top: 1px solid rgba(15,23,42,0.10);
          padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
        }
        .vi-live-cue {
          font-family: 'Lora', serif;
          font-size: 0.95rem;
          color: #5c4f38;
          text-align: center;
        }
        .vi-wave {
          display: flex;
          align-items: flex-end;
          justify-content: center;
          gap: 3px;
          height: 18px;
        }
        .vi-wave span {
          display: block;
          width: 3px;
          border-radius: 2px;
          background: rgba(122,90,16,0.45);
          height: 6px;
        }
        .vi-wave[data-live="true"] span {
          animation: vi-wave 0.9s ease-in-out infinite;
        }
        .vi-wave[data-live="true"] span:nth-child(2) { animation-delay: 0.15s; height: 12px; }
        .vi-wave[data-live="true"] span:nth-child(3) { animation-delay: 0.3s; height: 16px; }
        .vi-wave[data-live="true"] span:nth-child(4) { animation-delay: 0.15s; height: 12px; }
        @keyframes vi-wave {
          0%, 100% { transform: scaleY(0.55); }
          50% { transform: scaleY(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .vi-wave[data-live="true"] span { animation: none; }
        }
        .vi-finish-header {
          min-height: 44px;
          padding: 8px 12px;
          background: none;
          border: none;
          cursor: pointer;
          font-family: 'Nunito', sans-serif;
          font-size: 0.92rem;
          font-weight: 700;
          color: #5c4f38;
          -webkit-tap-highlight-color: transparent;
        }
        .vi-pip-slot {
          width: 120px;
          height: 120px;
          min-height: 120px;
          margin: 0 auto 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .vi-lang { position: relative; flex-shrink: 0; }
        .vi-lang-chip {
          min-height: 44px;
          padding: 8px 10px;
          background: none;
          border: none;
          cursor: pointer;
          font-family: 'Nunito', sans-serif;
          font-size: 0.85rem;
          font-weight: 700;
          color: #5c4f38;
          -webkit-tap-highlight-color: transparent;
        }
        .vi-lang-menu {
          position: absolute;
          right: 0;
          top: 100%;
          background: #fffdf8;
          border: 1px solid rgba(15,23,42,0.12);
          border-radius: 10px;
          box-shadow: 0 4px 16px rgba(15,23,42,0.12);
          z-index: 5;
          min-width: 128px;
        }
        .vi-lang-menu button {
          display: block;
          width: 100%;
          text-align: left;
          padding: 10px 14px;
          background: none;
          border: none;
          font-family: 'Nunito', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          color: #5c4f38;
          cursor: pointer;
        }
        .vi-type-link {
          background: none;
          border: none;
          color: #7a4a12;
          text-decoration: underline;
          text-underline-offset: 3px;
          font-family: 'Lora', serif;
          font-size: 0.95rem;
          min-height: 44px;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .vi-ready-back {
          background: none;
          border: none;
          color: #6b5c4e;
          font-family: 'Lora', serif;
          font-size: 0.92rem;
          min-height: 44px;
          cursor: pointer;
        }
        .vi-mic-hint {
          font-family: 'Lora', serif;
          font-size: 0.88rem;
          color: #7a5a10;
          text-align: center;
          margin: 12px auto 0;
          max-width: 300px;
          line-height: 1.45;
        }
        .vi-start-btn-pulse {
          animation: vi-connect-pulse 1.2s ease-in-out infinite;
        }
        @keyframes vi-connect-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.72; }
        }
        @media (prefers-reduced-motion: reduce) {
          .vi-start-btn-pulse { animation: none; }
        }
        .vi-surface {
          padding-bottom: 140px;
        }
        .vi-orb-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 28px 0 20px;
        }
        .vi-status-line {
          font-family: 'Lora', serif;
          font-size: 1.05rem;
          font-style: italic;
          color: #7a5a10;
          min-height: 1.5em;
          text-align: center;
          transition: opacity 0.3s;
        }
        /* Prominent listening cue — shown when connected and it's the user's turn */
        .vi-listening-cue {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 18px 0 4px;
        }
        .vi-listening-label {
          font-family: 'Lora', serif;
          font-size: 1.2rem;
          font-weight: 600;
          color: #0f172a;
          text-align: center;
          line-height: 1.4;
        }
        .vi-listening-sub {
          font-family: 'Lora', serif;
          font-size: 0.95rem;
          font-style: italic;
          color: #7a5a10;
          text-align: center;
        }
        /* Pulsing ring around Pip when listening */
        .vi-pulse-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 3px solid rgba(201,168,76,0.5);
          animation: vi-pulse 1.6s ease-out infinite;
        }
        .vi-pulse-ring-2 {
          position: absolute;
          width: 120%;
          height: 120%;
          top: -10%;
          left: -10%;
          border-radius: 50%;
          border: 2px solid rgba(201,168,76,0.25);
          animation: vi-pulse 1.6s ease-out 0.5s infinite;
        }
        @keyframes vi-pulse {
          0% { opacity: 0.9; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.35); }
        }
        @media (prefers-reduced-motion: reduce) {
          .vi-pulse-ring, .vi-pulse-ring-2 { animation: none; opacity: 0.4; }
        }
        /* Speaking cue */
        .vi-speaking-label {
          font-family: 'Lora', serif;
          font-size: 1.1rem;
          font-weight: 600;
          color: #7a5a10;
          text-align: center;
          font-style: italic;
          padding: 8px 0 0;
        }
        /* Fixed-height cue area so the listening<->speaking swap never reflows the
           page (was causing the whole screen to shake when Pip stopped talking). */
        .vi-cue-area {
          min-height: 92px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .vi-prompt-card {
          background: rgba(201,168,76,0.08);
          border: 1.5px solid rgba(201,168,76,0.25);
          border-radius: 14px;
          padding: 18px 20px;
          margin-bottom: 20px;
          font-family: 'Lora', serif;
        }
        .vi-prompt-framing {
          font-size: 0.88rem;
          color: #7a5a10;
          font-style: italic;
          margin-bottom: 6px;
        }
        .vi-prompt-text {
          font-size: clamp(1.1rem, 4vw, 1.35rem);
          color: #0f172a;
          font-style: italic;
          font-weight: 500;
          line-height: 1.45;
        }
        .vi-transcript {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 20px;
        }
        .vi-transcript-label {
          font-family: 'Lora', serif;
          font-size: 0.82rem;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          color: #9a8e7e;
          margin-bottom: 8px;
          font-weight: 600;
        }
        /* Reuse iv-bubble shapes for visual consistency */
        .vi-ai-bubble {
          align-self: flex-start;
          max-width: 88%;
          background: #fffdf8;
          border: 1.5px solid rgba(201,168,76,0.22);
          border-radius: 4px 20px 20px 20px;
          padding: 14px 18px;
          font-family: 'Lora', serif;
          font-size: 1.05rem;
          line-height: 1.6;
          color: #0f172a;
          box-shadow: 0 2px 8px rgba(15,23,42,0.04);
        }
        .vi-user-bubble {
          align-self: flex-end;
          max-width: 88%;
          background: rgba(15,23,42,0.88);
          color: #fff;
          border-radius: 20px 4px 20px 20px;
          padding: 14px 18px;
          font-family: 'Lora', serif;
          font-size: 1.05rem;
          line-height: 1.6;
        }
        .vi-dock {
          position: fixed;
          bottom: calc(64px + env(safe-area-inset-bottom, 0px));
          left: 0;
          right: 0;
          max-width: 1080px;
          margin: 0 auto;
          background: #efebe0;
          border-top: 1px solid rgba(15,23,42,0.10);
          padding: 14px 16px calc(14px + env(safe-area-inset-bottom, 0px));
          z-index: 80;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        @media (max-width: 640px) {
          .vi-dock { bottom: calc(58px + env(safe-area-inset-bottom, 0px)); }
        }
        /* Desktop: the fixed dock is a mobile pattern (offset for the bottom nav).
           On wider screens it can sit behind layout/3D-transform ancestors, hiding
           the buttons. Flow it inline instead so Start/Done are always visible. */
        @media (min-width: 641px) {
          .vi-surface { padding-bottom: 16px; }
          /* Stick the dock to the bottom of the viewport so the Done/write-it-up button
             is ALWAYS reachable, even when the conversation is taller than the screen
             (the previous static dock fell below the fold on short viewports). No 3D
             transform ancestor here, so sticky is safe (unlike in the book builder). */
          .vi-dock {
            position: sticky;
            bottom: 0;
            max-width: 460px;
            margin: 8px auto 0;
            background: #fbf8f1;
            border-top: 1px solid rgba(15,23,42,0.08);
            padding: 12px 16px 16px;
          }
        }
        .vi-start-btn {
          width: 100%;
          padding: 18px 24px;
          font-family: 'Lora', serif;
          font-size: 1.15rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          background: rgba(15,23,42,0.88);
          color: #fff;
          border: none;
          border-radius: 30px;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.1s;
          -webkit-tap-highlight-color: transparent;
          min-height: 56px;
        }
        .vi-start-btn:active { transform: scale(0.97); }
        .vi-start-btn:disabled { opacity: 0.4; cursor: default; }
        .vi-type-btn {
          width: 100%;
          min-height: 48px;
          padding: 12px 20px;
          font-family: 'Lora', serif;
          font-size: 1.05rem;
          font-weight: 700;
          color: #7a4a12;
          background: transparent;
          border: 1.5px solid rgba(146,64,14,0.38);
          border-radius: 18px;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .vi-type-btn:active { transform: scale(0.98); background: rgba(146,64,14,0.06); }
        .vi-done-btn {
          width: 100%;
          padding: 16px 24px;
          font-family: 'Lora', serif;
          font-size: 1.05rem;
          font-weight: 600;
          background: rgba(201,168,76,0.15);
          color: #5c4206;
          border: 1.5px solid rgba(201,168,76,0.4);
          border-radius: 30px;
          cursor: pointer;
          transition: background 0.2s, transform 0.1s;
          -webkit-tap-highlight-color: transparent;
          min-height: 52px;
        }
        .vi-done-btn:active { transform: scale(0.97); background: rgba(201,168,76,0.25); }
        .vi-done-btn:disabled { opacity: 0.4; cursor: default; }
        .vi-error-box {
          margin: 16px 0;
          padding: 18px 20px;
          background: rgba(220,80,60,0.06);
          border: 1.5px solid rgba(220,80,60,0.18);
          border-radius: 14px;
          font-family: 'Lora', serif;
          font-size: 1rem;
          color: #7c2d12;
          line-height: 1.55;
          text-align: center;
        }
        .vi-not-available-box {
          margin: 16px 0;
          padding: 20px 22px;
          background: rgba(201,168,76,0.08);
          border: 1.5px solid rgba(201,168,76,0.25);
          border-radius: 14px;
          font-family: 'Lora', serif;
          font-size: 1rem;
          color: #5c4206;
          line-height: 1.6;
          text-align: center;
        }
        .vi-tip-line {
          font-family: 'Lora', serif;
          font-size: 0.88rem;
          color: #7a6b5a;
          text-align: center;
          font-style: italic;
          padding: 0 4px;
        }
        /* ── Meter strip inside the fixed dock ── */
        /* The meter lives in .vi-dock so it is always on-screen regardless of
           how far down the user has scrolled through the transcript. The sticky
           approach inside the page scroll surface did not work because position:sticky
           requires the nearest scrolling ancestor to be the element that scrolls, and
           here the page/body is the scroller, not the .vi-surface wrapper. */
        .vi-meter-dock {
          padding: 8px 0 6px;
          border-bottom: 1px solid rgba(15,23,42,0.08);
          margin-bottom: 8px;
        }

        /* ── Progress meter ── */
        .vi-meter-wrap {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 0;
        }
        .vi-meter-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
        }
        .vi-meter-tier {
          font-family: 'Lora', serif;
          font-size: 1.05rem;
          font-weight: 700;
          line-height: 1.3;
          transition: color 0.4s ease;
        }
        .vi-meter-words {
          font-family: 'Lora', serif;
          font-size: 0.82rem;
          color: #9a8e7e;
          font-style: italic;
        }
        .vi-meter-track {
          position: relative;
          width: 100%;
          height: 10px;
          background: rgba(15,23,42,0.08);
          border-radius: 8px;
          overflow: hidden;
        }
        .vi-meter-fill {
          height: 100%;
          border-radius: 8px;
          transition: width 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.4s ease;
          position: relative;
        }
        .vi-meter-fill-amazing {
          animation: vi-meter-shimmer 1.6s ease-in-out infinite;
        }
        @keyframes vi-meter-shimmer {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.18); }
        }
        .vi-meter-tick {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 2px;
          background: rgba(255,255,255,0.7);
          transform: translateX(-50%);
        }
        .vi-meter-sub {
          font-family: 'Lora', serif;
          font-size: 0.88rem;
          font-style: italic;
          text-align: center;
          line-height: 1.4;
          transition: color 0.4s ease;
          min-height: 1.3em;
        }
        /* Amazing tier: celebratory ring around Pip */
        .vi-amazing-ring {
          position: absolute;
          inset: -14px;
          border-radius: 50%;
          border: 3px solid rgba(232, 128, 26, 0.55);
          animation: vi-amazing-pulse 1.2s ease-out infinite;
          pointer-events: none;
        }
        .vi-amazing-ring-2 {
          position: absolute;
          inset: -26px;
          border-radius: 50%;
          border: 2px solid rgba(232, 128, 26, 0.28);
          animation: vi-amazing-pulse 1.2s ease-out 0.4s infinite;
          pointer-events: none;
        }
        @keyframes vi-amazing-pulse {
          0% { opacity: 0.85; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.22); }
        }
        @media (prefers-reduced-motion: reduce) {
          .vi-meter-fill { transition: width 0.15s linear; }
          .vi-meter-fill-amazing { animation: none; }
          .vi-amazing-ring, .vi-amazing-ring-2 { animation: none; opacity: 0.35; }
        }
        /* Done button states tied to meter */
        .vi-done-btn-enabled {
          background: rgba(201,168,76,0.22);
          border-color: rgba(201,168,76,0.55);
          color: #3d2a00;
        }
        .vi-done-btn-not-yet {
          background: rgba(15,23,42,0.05);
          border-color: rgba(15,23,42,0.12);
          color: #9a8e7e;
          cursor: default;
          opacity: 0.75;
        }
        /* Cancel / Exit button in the dock — live and done phases */
        .vi-cancel-btn {
          background: none;
          border: 1.5px solid rgba(15,23,42,0.18);
          border-radius: 30px;
          padding: 10px 18px;
          font-family: 'Lora', serif;
          font-size: 0.92rem;
          color: #6b5c4e;
          cursor: pointer;
          transition: background 0.2s;
          min-height: 44px;
          -webkit-tap-highlight-color: transparent;
        }
        .vi-cancel-btn:active { background: rgba(15,23,42,0.06); }
        /* Empty-session inline message */
        .vi-empty-msg {
          margin: 0 0 6px;
          padding: 12px 16px;
          background: rgba(201,168,76,0.10);
          border: 1px solid rgba(201,168,76,0.30);
          border-radius: 12px;
          font-family: 'Lora', serif;
          font-size: 0.92rem;
          color: #5c4206;
          text-align: center;
          line-height: 1.5;
        }
        /* Exit-confirm overlay — reuses iv-switch-overlay visual language */
        .vi-exit-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15,23,42,0.55);
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        .vi-exit-card {
          background: #fffdf8;
          border-radius: 20px;
          padding: 28px 24px 20px;
          max-width: 380px;
          width: 100%;
          box-shadow: 0 8px 40px rgba(15,23,42,0.18);
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .vi-exit-title {
          font-family: 'Lora', serif;
          font-size: 1.2rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          text-align: center;
        }
        .vi-exit-sub {
          font-family: 'Lora', serif;
          font-size: 0.95rem;
          color: #5c4f38;
          margin: 0;
          text-align: center;
          line-height: 1.55;
        }
        .vi-exit-leave-btn {
          padding: 14px 20px;
          font-family: 'Lora', serif;
          font-size: 1rem;
          font-weight: 600;
          background: rgba(180,60,40,0.10);
          color: #7c2d12;
          border: 1.5px solid rgba(180,60,40,0.25);
          border-radius: 30px;
          cursor: pointer;
          min-height: 48px;
          -webkit-tap-highlight-color: transparent;
          transition: background 0.2s;
        }
        .vi-exit-leave-btn:active { background: rgba(180,60,40,0.18); }
        .vi-exit-keep-btn {
          padding: 14px 20px;
          font-family: 'Lora', serif;
          font-size: 1rem;
          font-weight: 700;
          background: rgba(201,168,76,0.18);
          color: #3d2a00;
          border: 1.5px solid rgba(201,168,76,0.50);
          border-radius: 30px;
          cursor: pointer;
          min-height: 48px;
          -webkit-tap-highlight-color: transparent;
          transition: background 0.2s;
        }
        .vi-exit-keep-btn:active { background: rgba(201,168,76,0.30); }
      `}</style>

      <div className="vi-chat-shell" ref={shellRef}>
          <header className="vi-chat-header">
            <button
              type="button"
              onClick={preCall ? onBack : handleExitLive}
              className="vi-header-btn"
            >
              {preCall ? (seedless ? 'Back' : 'Back to questions') : 'Cancel'}
            </button>
            {preCall ? (
            <div className="vi-lang">
              <button
                type="button"
                className="vi-lang-chip"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                onClick={() => setLangOpen((open) => !open)}
              >
                {lang === 'es' ? 'Español ▾' : 'English ▾'}
              </button>
              {langOpen && (
                <div className="vi-lang-menu" role="listbox">
                  <button type="button" onClick={() => chooseLang('en')}>English</button>
                  <button type="button" onClick={() => chooseLang('es')}>Español</button>
                </div>
              )}
            </div>
            ) : (
              ((phase === 'connected' && (hasSpokenOnce || userWordCount > 0)) || phase === 'done') ? (
                <button type="button" className="vi-finish-header" onClick={phase === 'done' ? () => finishUp() : handleFinishTap}>
                  {DONE_BUTTON_LABEL}
                </button>
              ) : <span />
            )}
          </header>
          <div className="vi-thread" ref={threadRef}>
            {preCall && selectedPrompt && (
              <div className="vi-prompt-card">
                <div className="vi-prompt-framing">
                  {isGiftBook
                    ? `A question about ${recipientName || 'them'}:`
                    : "Here's what we'll talk about:"}
                </div>
                {selectedPrompt.emoji && (
                  <div style={{ fontSize: 28, marginBottom: 6, textAlign: 'center' }}>
                    {selectedPrompt.emoji}
                  </div>
                )}
                <div className="vi-prompt-text">"{selectedPrompt.text}"</div>
              </div>
            )}
            {preCall && phase !== 'error' && (
              <div style={{ textAlign: 'center', paddingTop: 8, maxWidth: 380, margin: '0 auto' }}>
                <div className="vi-pip-slot">
                  <PipAvatar pose="greet" size={120} />
                </div>
                <h2 style={{ fontFamily: "'Lora', serif", fontSize: '1.7rem', fontWeight: 600, fontStyle: 'italic', color: '#0f172a', letterSpacing: '-.01em', margin: '0 0 12px' }}>
                  {lang === 'es' ? 'Cuando estés listo.' : 'Ready when you are.'}
                </h2>
                <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', lineHeight: 1.55, color: '#33404F', maxWidth: 330, margin: '0 auto' }}>
                  {lang === 'es'
                    ? 'Pip te hará una pregunta. Solo responde en voz alta, como si hablaras con un amigo. Tómate tu tiempo.'
                    : "Pip will ask you a question. Just answer out loud, like you're talking with a friend. Take your time."}
                </p>
                {micHint ? <p className="vi-mic-hint">{micHint}</p> : null}
              </div>
            )}
            {preCall && phase === 'error' && (
              errorMsg.includes('being set up') || errorMsg.includes('very soon') ? (
                <div className="vi-not-available-box">{errorMsg}</div>
              ) : (
                <div className="vi-error-box">{errorMsg}</div>
              )
            )}
            {!preCall && selectedPrompt && transcript.length === 0 && (
              <div className="vi-ai-bubble" data-vi-latest-pip="true">
                <div className="vi-prompt-framing">
                  {isGiftBook
                    ? `A question about ${recipientName || 'them'}:`
                    : "Here's what we'll talk about:"}
                </div>
                <div className="vi-prompt-text">"{selectedPrompt.text}"</div>
              </div>
            )}
            {!preCall && transcript.map((msg, i) => {
              const isAi = msg.role !== 'user';
              const lastAi = isAi && !transcript.slice(i + 1).some((m) => m.role !== 'user');
              return (
                <div
                  key={i}
                  className={isAi ? 'vi-ai-bubble' : 'vi-user-bubble'}
                  data-vi-latest-pip={lastAi ? 'true' : undefined}
                >
                  {msg.content}
                </div>
              );
            })}
            {!preCall && emptyWriteupMsg && (
              <div className="vi-empty-msg" role="alert">{emptyWriteupMsg}</div>
            )}
            {!preCall && showLengthNudge && (
              <p className="vi-tip-line">You've captured a beautiful amount for this story. Finish whenever you're ready, or keep going.</p>
            )}
            {!preCall && phase === 'done' && (
              <div className="vi-not-available-box">{FINISH_CARD_LINE}</div>
            )}
            {!preCall && phase === 'writingup' && (
              <div className="vi-ai-bubble" data-vi-latest-pip="true">
                Gathering everything you shared...
              </div>
            )}
          </div>
          {preCall && (
          <div className="vi-ready-dock">
            {phase === 'error' ? (
              <div className="vi-error-actions" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
                <button
                  type="button"
                  className="vi-start-btn"
                  onClick={handleStart}
                  style={{
                    background: 'linear-gradient(180deg,#AE6015,#A85A0F)',
                    color: '#fff', boxShadow: '0 4px 16px rgba(168,90,15,.3)', borderRadius: 18,
                  }}
                >
                  {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
                </button>
                {onSwitchToText && (
                  <button type="button" className="vi-type-link" onClick={onSwitchToText}>
                    {lang === 'es' ? 'Escribir en su lugar' : 'Type instead'}
                  </button>
                )}
                <button type="button" className="vi-ready-back" onClick={handleReturnToReady}>
                  {lang === 'es' ? 'Volver' : 'Go back'}
                </button>
              </div>
            ) : (
              <>
                <button
                  className={`vi-start-btn${isConnecting ? ' vi-start-btn-pulse' : ''}`}
                  onClick={handleStart}
                  disabled={isConnecting}
                  style={{
                    background: 'linear-gradient(180deg,#AE6015,#A85A0F)',
                    color: '#fff', boxShadow: '0 4px 16px rgba(168,90,15,.3)', borderRadius: 18,
                    cursor: isConnecting ? 'default' : 'pointer',
                    opacity: isConnecting ? 0.9 : 1,
                  }}
                >
                  {isConnecting ? 'Connecting...' : (lang === 'es' ? 'Toca para empezar' : 'Tap to begin')}
                </button>
                {onSwitchToText && !isConnecting && (
                  <button
                    type="button"
                    className="vi-type-btn"
                    onClick={onSwitchToText}
                  >
                    {lang === 'es' ? 'Prefiero escribir' : "I'd rather type"}
                  </button>
                )}
              </>
            )}
          </div>
          )}
          {!preCall && (
            <div className="vi-live-dock">
              {phase === 'connected' && (
                <>
                  <div className="vi-wave" data-live="true" aria-hidden="true">
                    <span /><span /><span /><span /><span />
                  </div>
                  <div className="vi-live-cue">
                    {isSpeaking ? 'Pip is talking...' : (hasSpokenOnce ? 'Your turn' : "Go ahead, I'm listening")}
                  </div>
                </>
              )}
              {phase === 'writingup' && (
                <div className="vi-live-cue">Gathering your story...</div>
              )}
              {phase === 'done' && (
                <div className="vi-live-cue">When you are ready, finish from the header.</div>
              )}
            </div>
          )}
        </div>

      {exitConfirm && (
        <div
          style={EXIT_GUARD_SCRIM}
          onClick={() => setExitConfirm(false)}
          role="dialog" aria-modal="true" aria-label="Finish or leave this interview"
        >
          <div style={EXIT_GUARD_CARD} onClick={(e) => e.stopPropagation()}>
            <p style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 700, color: '#2b2b2b', lineHeight: 1.3, margin: '0 0 8px' }}>{EXIT_GUARD_TITLE}</p>
            <p style={{ fontFamily: "'Lora', serif", fontSize: 15, color: '#6b5c4e', lineHeight: 1.5, margin: '0 0 18px' }}>{EXIT_GUARD_SUB}</p>
            <button style={CTA_PRIMARY} onClick={() => { setExitConfirm(false); finishUp({ force: true }); }}>
              {DONE_BUTTON_LABEL}
            </button>
            <div style={{ height: 10 }} />
            <button
              style={CTA_SECONDARY}
              onClick={() => {
                setExitConfirm(false);
                releaseSession(); // F-91: end session + stop mic immediately (unmount also releases)
                if (onCancel) { onCancel(); } else { onBack(); }
              }}
            >
              {EXIT_GUARD_LEAVE}
            </button>
          </div>
        </div>
      )}

    </>
  );
};

// @elevenlabs/react v1.6.7 requires useConversation to run inside a
// ConversationProvider. Wrapping it here keeps that an internal detail, so
// StRecordPage can render <VoiceInterview/> directly.
// Props forwarded: selectedPrompt, onDone, onBack, onCancel, onSwitchToText, isGiftBook, recipientName, occasionId, pack
const VoiceInterview = (props) => (
  <ConversationProvider>
    <VoiceInterviewInner {...props} />
  </ConversationProvider>
);

export default VoiceInterview;
